import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import { UseOutletContextAdmin } from './Admin';
import { StateLanguage } from '../../../languages/config/StateLanguage';
import { useSubscription } from '../../../contexts/SubscriptionContext';
import { useCatalog } from '../../../contexts/CatalogContext';
import { useUpgradeModal } from '../../../contexts/UpgradeModalContext';
import { ApiDigitalAuthSubscription } from '../../../services/auth/subscription.service';
import { BillingDA } from '../../../services/admin/billing.service';
import { authApiKey } from '../../../scripts/authentication.script';
import { dateFormat } from '../../../scripts/date.time.format.script';
import { payInvoice } from '../../../scripts/billing.payment';
import { GlobalSwal } from '../../../styles/swal.style';
import { BillingInvoice } from '../../../models/billing.model';
import { SubscriptionQuota } from '../../../models/subscription.snapshot.model';
import { getCyclePriceInfo } from '../../../scripts/cycle.pricing';

export interface SubscriptionPageProps {};

const SubscriptionPage: React.FunctionComponent<SubscriptionPageProps> = () => {
  const {setRoute, companyForUser} = UseOutletContextAdmin();
  const {lang} = StateLanguage();
  const navigate = useNavigate();
  const { snapshot, refreshSubscription, can: canSub } = useSubscription();
  const { plans } = useCatalog();
  const { openUpgradeModal } = useUpgradeModal();
  const [mounted, setMounted] = useState(false);
  const [loadIndicator, setLoadIndicator] = useState('off');

  // Amount due state
  const [payableInvoices, setPayableInvoices] = useState<BillingInvoice[]>([]);
  const [payingAmountDue, setPayingAmountDue] = useState(false);
  const canManage = canSub('manage', 'digital:auth:entity:subscription:*').allowed;

  useEffect(() => {
    setMounted(true);
    setRoute({path: {root: lang.labels.administration, branch: lang.labels.subscriptionMySubscription}, company: true});
    return () => setMounted(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load payable invoices for "amount due" block
  useEffect(() => {
    if (!companyForUser?.id) return;
    let cancelled = false;
    (async () => {
      try {
        const response = await BillingDA.getInvoices(companyForUser.id, 1, 100);
        if (cancelled) return;
        if (response.status === 200 && response.data) {
          const items: BillingInvoice[] = response.data.items ?? response.data.invoices ?? (Array.isArray(response.data) ? response.data : []);
          setPayableInvoices(items.filter(inv => inv.payable));
        }
      } catch {
        // Silently ignore — the block simply won't appear
      }
    })();
    return () => { cancelled = true; };
  }, [companyForUser?.id]);

  const handlePayAmountDue = async () => {
    if (!companyForUser?.id || payableInvoices.length === 0) return;
    setPayingAmountDue(true);
    try {
      if (payableInvoices.length === 1) {
        await payInvoice(payableInvoices[0], companyForUser.id, navigate, {
          error: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
          ok: lang.labels.okGotIt,
        });
      } else {
        navigate('/main/admin/subscription/invoices');
      }
    } finally {
      setPayingAmountDue(false);
    }
  };

  const interpolate = (template: string, vars: Record<string, string | number>): string => {
    let result = template;
    for (const [key, value] of Object.entries(vars)) {
      result = result.replace(`{${key}}`, String(value));
    }
    return result;
  };

  const fmtDate = useCallback((iso?: string): string => {
    if (!iso) return '';
    return dateFormat(lang.code, iso);
  }, [lang.code]);

  const daysUntil = (iso?: string): number => {
    if (!iso) return 0;
    const target = new Date(iso).getTime();
    const now = Date.now();
    return Math.max(0, Math.ceil((target - now) / (1000 * 60 * 60 * 24)));
  };

  const statusLabel = (status?: string): string => {
    switch (status) {
      case 'active': return lang.labels.subscriptionStatusActive;
      case 'trial': return lang.labels.subscriptionStatusTrial;
      case 'past_due': return lang.labels.subscriptionStatusPastDue;
      case 'expired': return lang.labels.subscriptionStatusExpired;
      case 'cancelled': return lang.labels.subscriptionStatusCancelled;
      default: return '';
    }
  };

  const statusBadgeClass = (status?: string): string => {
    switch (status) {
      case 'active': return 'badge-light-success';
      case 'trial': return 'badge-light-info';
      case 'past_due': return 'badge-light-warning';
      case 'expired': return 'badge-light-danger';
      case 'cancelled': return 'badge-light-dark';
      default: return 'badge-light-secondary';
    }
  };

  const billingCycleLabel = (cycle?: string): string => {
    if (cycle === 'monthly') return lang.labels.monthly;
    if (cycle === 'yearly') return lang.labels.annual;
    return cycle ?? '';
  };

  const pendingPlanName = (): string => {
    const pendingId = snapshot?.subscription?.pending_plan_id;
    if (!pendingId) return '';
    const p = plans.find(pl => pl.id === pendingId);
    return p?.name ?? pendingId;
  };

  // --- Actions ---

  const handleCancelSubscription = async () => {
    const result = await GlobalSwal.fire({
      title: lang.labels.subscriptionCancelConfirmTitle,
      text: lang.labels.subscriptionCancelConfirmText,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: lang.labels.subscriptionCancelSubscription,
      cancelButtonText: lang.labels.cancel,
    });
    if (!result.isConfirmed) return;

    const apiKey = authApiKey();
    if (!apiKey) return;
    const subId = snapshot?.subscription?.id;
    if (!subId) {
      GlobalSwal.fire({ text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain, icon: 'error' });
      return;
    }

    setLoadIndicator('on');
    try {
      const response = await ApiDigitalAuthSubscription.cancelSubscription(apiKey, subId);
      if (response.status === 200) {
        await refreshSubscription();
        GlobalSwal.fire({
          title: lang.labels.subscriptionCancelledSuccess,
          icon: 'success',
          showConfirmButton: false,
          timer: 2200,
        });
      } else {
        GlobalSwal.fire({
          text: response.data?.message ?? response.message,
          icon: 'error',
          confirmButtonText: lang.labels.okGotIt,
        });
      }
    } catch {
      GlobalSwal.fire({ text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain, icon: 'error' });
    } finally {
      setLoadIndicator('off');
    }
  };

  const handleReactivate = async () => {
    const result = await GlobalSwal.fire({
      title: lang.labels.subscriptionCancelResiliation,
      text: lang.labels.subscriptionCancelResiliationConfirmText,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: lang.labels.subscriptionCancelResiliation,
      cancelButtonText: lang.labels.cancel,
    });
    if (!result.isConfirmed) return;

    const apiKey = authApiKey();
    if (!apiKey) return;
    const subId = snapshot?.subscription?.id;
    if (!subId) {
      GlobalSwal.fire({ text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain, icon: 'error' });
      return;
    }

    setLoadIndicator('on');
    try {
      const response = await ApiDigitalAuthSubscription.reactivateSubscription(apiKey, subId);
      if (response.status === 200) {
        await refreshSubscription();
        GlobalSwal.fire({
          title: lang.labels.subscriptionReactivatedSuccess,
          icon: 'success',
          showConfirmButton: false,
          timer: 2200,
        });
      } else {
        const errorCode = response.data?.code;
        if (response.status === 409 && errorCode === 'NOTHING_TO_UNDO') {
          GlobalSwal.fire({
            text: lang.labels.subscriptionErrorNothingToUndo,
            icon: 'info',
            confirmButtonText: lang.labels.okGotIt,
          });
        } else {
          GlobalSwal.fire({
            text: response.data?.message ?? response.message,
            icon: 'error',
            confirmButtonText: lang.labels.okGotIt,
          });
        }
      }
    } catch {
      GlobalSwal.fire({ text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain, icon: 'error' });
    } finally {
      setLoadIndicator('off');
    }
  };

  const handleCancelPending = async () => {
    const currentPlanName = plan?.name ?? '';
    const pendingName = pendingPlanName();

    const result = await GlobalSwal.fire({
      title: lang.labels.subscriptionCancelPendingConfirmTitle,
      text: interpolate(lang.labels.subscriptionCancelPendingConfirmText, {
        currentPlan: currentPlanName,
        pendingPlan: pendingName,
      }),
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: lang.labels.subscriptionCancelPendingChange,
      cancelButtonText: lang.labels.cancel,
    });
    if (!result.isConfirmed) return;

    const apiKey = authApiKey();
    if (!apiKey) return;
    const subId = snapshot?.subscription?.id;
    if (!subId) {
      GlobalSwal.fire({ text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain, icon: 'error' });
      return;
    }

    setLoadIndicator('on');
    try {
      const response = await ApiDigitalAuthSubscription.cancelPendingChange(apiKey, subId);
      if (response.status === 200) {
        await refreshSubscription();
        GlobalSwal.fire({
          title: lang.labels.subscriptionPendingChangeCancelled,
          icon: 'success',
          showConfirmButton: false,
          timer: 2200,
        });
      } else {
        GlobalSwal.fire({
          text: response.data?.message ?? response.message,
          icon: 'error',
          confirmButtonText: lang.labels.okGotIt,
        });
      }
    } catch {
      GlobalSwal.fire({ text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain, icon: 'error' });
    } finally {
      setLoadIndicator('off');
    }
  };

  if (!mounted) return null;

  const sub = snapshot?.subscription;
  const plan = sub?.plan;
  const quotas = snapshot?.subscription?.quotas;
  const status = sub?.status;
  const isTrial = status === 'trial';
  const isCancelled = status === 'cancelled' && sub?.cancel_at_period_end;
  const isExpired = status === 'expired';
  const hasPendingChange = !!sub?.pending_plan_id;

  const renderQuotaValue = (q: SubscriptionQuota): string => {
    if (q.limit === -1) return lang.labels.subscriptionUnlimited;
    return interpolate(lang.labels.subscriptionQuotaUsage, { current: q.current, limit: q.limit });
  };

  const renderQuotaBar = (q: SubscriptionQuota): React.ReactNode => {
    if (q.limit === -1) return null;
    const pct = q.limit > 0 ? Math.min(100, Math.round((q.current / q.limit) * 100)) : 0;
    const barColor = pct >= 90 ? '#F64E60' : pct >= 70 ? '#FFA800' : '#3699FF';
    return (
      <div style={{ width: '100%', height: 6, backgroundColor: '#eee', borderRadius: 3, marginTop: 4 }}>
        <div style={{ width: `${pct}%`, height: '100%', backgroundColor: barColor, borderRadius: 3 }} />
      </div>
    );
  };

  return (
    <div className="d-flex flex-column flex-column-fluid">
      <div className="d-flex flex-column flex-lg-row">
        <div className="flex-lg-row-fluid me-lg-5 order-1">
          {/* Current Plan Card */}
          <div className="card card-flush mb-5">
            <div className="card-header">
              <div className="card-title">
                <h3 className="fw-bolder">{lang.labels.subscriptionCurrentPlan}</h3>
              </div>
              {status && (
                <div className="card-toolbar">
                  <span className={`badge ${statusBadgeClass(status)} fs-7`}>{statusLabel(status)}</span>
                </div>
              )}
            </div>
            <div className="card-body">
              {!snapshot ? (
                <div className="text-center py-10">
                  <span className="spinner-border spinner-border-sm text-primary"></span>
                </div>
              ) : (
                <>
                  <table className="table gs-0 gy-2 m-0">
                    <tbody>
                      <tr>
                        <td className="w-200px fw-bold text-muted">{lang.labels.subscriptionCurrentPlan}:</td>
                        <td className="min-w-200px fw-bold fs-6">
                          {plan?.name ?? lang.labels.subscriptionNoPlan}
                          {plan?.is_free && <span className="badge badge-light-success ms-2">{lang.labels.subscriptionFreePlan}</span>}
                        </td>
                      </tr>
                      {(sub?.plan?.current_billing_cycle ?? sub?.current_billing_cycle) && (
                        <tr>
                          <td className="w-200px fw-bold text-muted">{lang.labels.subscriptionBillingCycle}:</td>
                          <td className="min-w-200px fw-bold fs-6">{billingCycleLabel(sub.plan?.current_billing_cycle ?? sub.current_billing_cycle)}</td>
                        </tr>
                      )}
                      <tr>
                        <td className="w-200px fw-bold text-muted">{lang.labels.status}:</td>
                        <td className="min-w-200px fw-bold fs-6">{statusLabel(status)}</td>
                      </tr>
                      {sub?.current_period_end && !isExpired && (
                        <tr>
                          <td className="w-200px fw-bold text-muted">{lang.labels.subscriptionNextRenewal}:</td>
                          <td className="min-w-200px fw-bold fs-6">{fmtDate(sub.current_period_end)}</td>
                        </tr>
                      )}
                    </tbody>
                  </table>

                  {/* Annual incentive — shown when currently on monthly billing */}
                  {(() => {
                    const cycle = sub?.plan?.current_billing_cycle ?? sub?.current_billing_cycle;
                    if (cycle !== 'monthly' || !plan) return null;
                    const matchedPlan = plans.find(p => p.id === plan.id);
                    if (!matchedPlan) return null;
                    const yearlyInfo = getCyclePriceInfo(matchedPlan, 'yearly');
                    if (!yearlyInfo.savingsPercent || yearlyInfo.savingsPercent <= 0) return null;
                    return (
                      <div className="notice d-flex bg-light-success rounded border-success border border-dashed p-4 mt-5">
                        <div className="d-flex flex-stack flex-grow-1">
                          <div className="fw-bold">
                            <div className="fs-6 text-gray-700">
                              {interpolate(lang.labels.subscriptionAnnualSavingsHint, { percent: yearlyInfo.savingsPercent })}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })()}

                  {/* Trial info */}
                  {isTrial && sub?.trial_end && (
                    <div className="notice d-flex bg-light-primary rounded border-primary border border-dashed p-4 mt-5">
                      <div className="d-flex flex-stack flex-grow-1">
                        <div className="fw-bold">
                          <div className="fs-6 text-gray-700">
                            {interpolate(lang.labels.subscriptionTrialEndsOn, { date: fmtDate(sub.trial_end) })}
                          </div>
                          <div className="text-gray-500 fs-7">
                            {interpolate(lang.labels.subscriptionTrialDaysLeft, { days: daysUntil(sub.trial_end) })}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Pending change */}
                  {hasPendingChange && (
                    <div className="notice d-flex bg-light-warning rounded border-warning border border-dashed p-4 mt-5">
                      <div className="d-flex flex-stack flex-grow-1 flex-wrap gap-2">
                        <div className="fw-bold">
                          <div className="fs-6 text-gray-700">
                            {lang.labels.subscriptionPendingChange}
                          </div>
                          <div className="text-gray-500 fs-7">
                            {interpolate(lang.labels.subscriptionPendingChangeDetail, {
                              plan: pendingPlanName(),
                              date: fmtDate((() => {
                                // During trial, the effective date for a downgrade is trial_end + one cycle,
                                // not current_period_end (which equals trial_end during trial)
                                if (isTrial && sub?.trial_end) {
                                  const d = new Date(sub.trial_end);
                                  const cycle = sub.plan?.current_billing_cycle ?? sub.current_billing_cycle;
                                  if (cycle === 'yearly') {
                                    d.setFullYear(d.getFullYear() + 1);
                                  } else {
                                    d.setMonth(d.getMonth() + 1);
                                  }
                                  return d.toISOString();
                                }
                                return sub?.current_period_end ?? '';
                              })()),
                            })}
                          </div>
                        </div>
                        {canManage && (
                          <button
                            className="btn btn-sm btn-light-warning"
                            type="button"
                            data-kt-indicator={loadIndicator}
                            onClick={handleCancelPending}
                          >
                            {lang.labels.subscriptionCancelPendingChange}
                          </button>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Action buttons — state-based */}
                  {canManage && (
                    <div className="d-flex gap-3 mt-8">
                      {!sub || (!isTrial && !status) ? (
                        /* No subscription at all */
                        <button
                          className="btn btn-primary"
                          type="button"
                          onClick={() => openUpgradeModal()}
                        >
                          {lang.labels.subscriptionChooseMyOffer}
                        </button>
                      ) : (isTrial || isExpired) && sub?.plan_chosen === false ? (
                        /* Trial or expired, no plan chosen yet → "Choisir mon offre" */
                        <button
                          className="btn btn-primary"
                          type="button"
                          onClick={() => openUpgradeModal()}
                        >
                          {lang.labels.subscriptionChooseMyOffer}
                        </button>
                      ) : isTrial && sub?.plan_chosen === true && payableInvoices.length > 0 ? (
                        /* Trial, plan chosen, unpaid invoice */
                        <>
                          <button
                            className="btn btn-primary"
                            type="button"
                            disabled={payingAmountDue}
                            onClick={handlePayAmountDue}
                          >
                            {payingAmountDue
                              ? <span className="spinner-border spinner-border-sm"></span>
                              : lang.labels.subscriptionPayMyInvoice
                            }
                          </button>
                          <button
                            className="btn btn-light-primary"
                            type="button"
                            onClick={() => openUpgradeModal()}
                          >
                            {lang.labels.subscriptionChangePlan}
                          </button>
                        </>
                      ) : (
                        /* Active or trial with plan chosen and no unpaid invoice */
                        <button
                          className="btn btn-primary"
                          type="button"
                          onClick={() => openUpgradeModal()}
                        >
                          {lang.labels.subscriptionChangePlan}
                        </button>
                      )}
                      {(status === 'active' || isTrial) && !isCancelled && (
                        <button
                          className="btn btn-light-danger"
                          type="button"
                          data-kt-indicator={loadIndicator}
                          onClick={handleCancelSubscription}
                        >
                          {lang.labels.subscriptionCancelSubscription}
                        </button>
                      )}
                      {sub?.cancel_at_period_end === true && (
                        <button
                          className="btn btn-light-success"
                          type="button"
                          data-kt-indicator={loadIndicator}
                          onClick={handleReactivate}
                        >
                          {lang.labels.subscriptionCancelResiliation}
                        </button>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Amount due block — only if payable invoices exist */}
          {payableInvoices.length > 0 && (() => {
            const totalDue = payableInvoices.reduce((sum, inv) => sum + (inv.amount ?? 0), 0);
            const currency = payableInvoices[0]?.currency ?? 'CHF';
            const earliestDue = payableInvoices
              .map(inv => inv.valid_until)
              .filter((d): d is string => !!d)
              .sort()[0];
            return (
              <div className="card card-flush mb-5">
                <div className="card-body d-flex align-items-center justify-content-between flex-wrap gap-4 py-6">
                  <div>
                    <div className="fw-bold text-gray-700 fs-7 mb-1">{lang.labels.billingAmountDue}</div>
                    <div className="fw-bolder fs-2" style={{ fontVariantNumeric: 'tabular-nums' }}>
                      {currency} {totalDue.toFixed(2)}
                    </div>
                    {earliestDue && (
                      <div className="text-gray-600 fs-7">
                        {interpolate(lang.labels.billingDueDate, { date: fmtDate(earliestDue) })}
                      </div>
                    )}
                    {payableInvoices.length > 1 && (
                      <div className="text-gray-500 fs-7">
                        {interpolate(lang.labels.billingAmountDueCount, { count: payableInvoices.length })}
                      </div>
                    )}
                  </div>
                  {canManage && (
                    <button
                      className="btn btn-primary"
                      type="button"
                      disabled={payingAmountDue}
                      onClick={handlePayAmountDue}
                    >
                      {payingAmountDue
                        ? <span className="spinner-border spinner-border-sm"></span>
                        : payableInvoices.length === 1
                          ? lang.labels.subscriptionPayNow
                          : lang.labels.billingViewInvoices
                      }
                    </button>
                  )}
                </div>
              </div>
            );
          })()}

          {/* Quotas Card */}
          {quotas && Object.keys(quotas).length > 0 && (
            <div className="card card-flush mb-5">
              <div className="card-header">
                <div className="card-title">
                  <h3 className="fw-bolder">{lang.labels.subscriptionQuotas}</h3>
                </div>
              </div>
              <div className="card-body">
                <div className="row g-5">
                  {Object.entries(quotas).map(([key, q]) => (
                    <div className="col-md-4" key={key}>
                      <div className="border rounded p-4">
                        <div className="fw-bold text-gray-700 fs-7 text-capitalize mb-1">
                          {key.replace(/[._]/g, ' ')}
                        </div>
                        <div className="fw-bolder fs-5">
                          {renderQuotaValue(q)}
                        </div>
                        {renderQuotaBar(q)}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Navigation to billing screens */}
          <div className="card card-flush mb-5">
            <div className="card-body">
              <div className="d-flex flex-wrap gap-3">
                <button
                  className="btn btn-light-primary"
                  type="button"
                  onClick={() => navigate('/main/admin/subscription/invoices')}
                >
                  {lang.labels.billingInvoices}
                </button>
                {canManage && (
                  <button
                    className="btn btn-light-primary"
                    type="button"
                    onClick={() => navigate('/main/admin/subscription/payment-methods')}
                  >
                    {lang.labels.billingPaymentMethods}
                  </button>
                )}
                <button
                  className="btn btn-light-primary"
                  type="button"
                  onClick={() => navigate('/main/admin/subscription/addons')}
                >
                  {lang.labels.addonManageAddons}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionPage;
